import "./EnrollmentsTable.css";

import React, { useEffect, useState } from "react";
import { createClient } from "@supabase/supabase-js";

// Initialize Supabase (you already have this)
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// 1. LOGIN COMPONENT
function AdminLogin({ onLoginSuccess }) {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);

	const handleLogin = async () => {
		setError("");
		setLoading(true);

		try {
			const { data, error } = await supabase.auth.signInWithPassword({
				email,
				password,
			});

			if (error) throw error;

			// Check if user is admin
			const isAdmin = data.user?.user_metadata?.role === "admin";

			if (!isAdmin) {
				await supabase.auth.signOut();
				throw new Error("Unauthorized: Admin access only");
			}

			onLoginSuccess();
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	const handleKeyPress = (e) => {
		if (e.key === "Enter") {
			handleLogin();
		}
	};

	return (
		<div className="login-container">
			<div className="login-box">
				<div className="login-header">
					<div className="login-icon">🔐</div>
					<h2 className="login-title">Admin Access</h2>
					<p className="login-subtitle">Enter your credentials to continue</p>
				</div>
				<div className="login-form">
					<div className="input-group">
						<label className="input-label">Email Address</label>
						<input
							type="email"
							placeholder="admin@example.com"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							onKeyPress={handleKeyPress}
							className="login-input"
						/>
					</div>
					<div className="input-group">
						<label className="input-label">Password</label>
						<input
							type="password"
							placeholder="••••••••"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							onKeyPress={handleKeyPress}
							className="login-input"
						/>
					</div>
					{error && <div className="error-message">{error}</div>}
					<button
						onClick={handleLogin}
						disabled={loading}
						className="login-button"
					>
						{loading ? (
							<>
								<span className="spinner"></span>
								Authenticating...
							</>
						) : (
							"Sign In"
						)}
					</button>
				</div>
			</div>
		</div>
	);
}

// 2. PROTECTED ADMIN PAGE (Your existing component with auth)
function EnrollmentsTable({ onLogout }) {
	const [enrollments, setEnrollments] = useState([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [sortBy, setSortBy] = useState("date");

	const fetchEnrollments = async () => {
		const { data, error } = await supabase
			.from("enrollments")
			.select("*")
			.order("created_at", { ascending: false });

		if (error) console.error("Error fetching data:", error);
		else {
			// Initialize remaining_amount if null
			const updatedData = data.map((item) => ({
				...item,
				remaining_amount: item.remaining_amount ?? item.total_amount,
				paid_amount: item.paid_amount ?? 0,
			}));
			setEnrollments(updatedData);
		}
	};

	useEffect(() => {
		fetchEnrollments();
	}, []);

	// Replace the handleTogglePayment function with this:
	const handleTogglePayment = async (
		id,
		currentStatus,
		totalAmount,
		remainingAmount,
		paidAmount
	) => {
		if (remainingAmount > 0) {
			const payment = prompt(
				`Enter the amount paid:\nTotal: ₹${totalAmount}\nAlready Paid: ₹${paidAmount}\nRemaining: ₹${remainingAmount}`
			);

			if (payment === null) return; // User clicked cancel

			const newPayment = parseFloat(payment);

			if (isNaN(newPayment) || newPayment <= 0) {
				alert("Please enter a valid amount");
				return;
			}

			if (newPayment > remainingAmount) {
				alert(
					`Payment amount cannot exceed remaining amount of ₹${remainingAmount}`
				);
				return;
			}

			const updatedPaidAmount = paidAmount + newPayment;
			const updatedRemainingAmount = totalAmount - updatedPaidAmount;
			const newStatus = updatedRemainingAmount === 0 ? "paid" : "pending";

			const { error } = await supabase
				.from("enrollments")
				.update({
					payment_status: newStatus,
					paid_amount: updatedPaidAmount,
					remaining_amount: updatedRemainingAmount,
				})
				.eq("id", id);

			if (!error) {
				setEnrollments((prev) =>
					prev.map((item) =>
						item.id === id
							? {
									...item,
									payment_status: newStatus,
									paid_amount: updatedPaidAmount,
									remaining_amount: updatedRemainingAmount,
							  }
							: item
					)
				);
			}
		}
	};
	const filteredData = enrollments
		.filter((item) =>
			item.name.toLowerCase().includes(searchQuery.toLowerCase())
		)
		.sort((a, b) => {
			if (sortBy === "pending") {
				return a.payment_status === "pending" && b.payment_status !== "pending"
					? -1
					: 1;
			}
			return new Date(b.created_at) - new Date(a.created_at);
		});

	return (
		<div className="enrollments-container">
			<div className="dashboard-header">
				<div className="header-content">
					<h1 className="heading">Enrollments Dashboard</h1>
					<p className="header-subtitle">
						Manage and track all student enrollments
					</p>
				</div>
				<button onClick={onLogout} className="logout-btn">
					<span className="logout-icon">🚪</span>
					Logout
				</button>
			</div>

			<div className="controls">
				<input
					type="text"
					placeholder="🔍 Search by name..."
					className="search-input"
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
				/>

				<button
					className="sort-btn"
					onClick={() =>
						setSortBy((prev) => (prev === "date" ? "pending" : "date"))
					}
				>
					<span className="sort-icon">⇅</span>
					Sort by: {sortBy === "date" ? "Date" : "Pending First"}
				</button>
			</div>

			<table className="enrollments-table">
				<thead>
					<tr>
						<th>Date</th>
						<th>Name</th>
						<th>Email</th>
						<th>Courses</th>
						<th>Total (₹)</th>
						<th>Paid (₹)</th>
						<th>Remaining (₹)</th>
						<th>Payment Mode</th>
						<th>Status</th>
					</tr>
				</thead>
				<tbody>
					{filteredData.length > 0 ? (
						filteredData.map((item) => (
							<tr key={item.id}>
								<td>{new Date(item.created_at).toLocaleDateString()}</td>
								<td>{item.name}</td>
								<td>{item.email}</td>
								<td>{item.selected_courses.join(", ")}</td>
								<td>{item.total_amount}</td>
								<td>{item.paid_amount || 0}</td>
								<td
									style={{
										fontWeight: item.remaining_amount > 0 ? "bold" : "normal",
									}}
								>
									{item.remaining_amount || 0}
								</td>
								<td>{item.payment_mode}</td>
								<td>
									<span
										className={`status-tag ${
											item.payment_status === "pending" ? "pending" : "paid"
										}`}
										onClick={() =>
											item.payment_mode === "cash" &&
											item.payment_status !== "paid"
												? handleTogglePayment(
														item.id,
														item.payment_status,
														item.total_amount,
														item.remaining_amount || item.total_amount,
														item.paid_amount || 0
												  )
												: null
										}
										style={{
											cursor:
												item.payment_mode === "cash" &&
												item.payment_status !== "paid"
													? "pointer"
													: "default",
										}}
									>
										{item.payment_status.toUpperCase()}
									</span>
								</td>
							</tr>
						))
					) : (
						<tr>
							<td colSpan="9" className="no-data">
								No enrollments found.
							</td>
						</tr>
					)}
				</tbody>
			</table>
		</div>
	);
}

// 3. MAIN APP WITH AUTH LOGIC
export default function AdminApp() {
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		checkAuth();

		// Listen for auth changes
		const { data: authListener } = supabase.auth.onAuthStateChange(
			async (event, session) => {
				if (event === "SIGNED_IN" && session) {
					const isAdmin = session.user?.user_metadata?.role === "admin";
					setIsAuthenticated(isAdmin);
				} else if (event === "SIGNED_OUT") {
					setIsAuthenticated(false);
				}
			}
		);

		return () => {
			authListener.subscription.unsubscribe();
		};
	}, []);

	const checkAuth = async () => {
		try {
			const {
				data: { session },
			} = await supabase.auth.getSession();

			if (session) {
				const isAdmin = session.user?.user_metadata?.role === "admin";
				setIsAuthenticated(isAdmin);
			} else {
				setIsAuthenticated(false);
			}
		} catch (error) {
			console.error("Auth check error:", error);
			setIsAuthenticated(false);
		} finally {
			setLoading(false);
		}
	};

	const handleLogout = async () => {
		await supabase.auth.signOut();
		setIsAuthenticated(false);
	};

	if (loading) {
		return (
			<div className="loading-container">
				<div className="loading-spinner"></div>
				<p className="loading-text">Loading...</p>
			</div>
		);
	}

	return isAuthenticated ? (
		<EnrollmentsTable onLogout={handleLogout} />
	) : (
		<AdminLogin onLoginSuccess={() => setIsAuthenticated(true)} />
	);
}
